import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { Leaf, TrendingDown, TrendingUp, Award, AlertTriangle, CheckCircle, ArrowRight, Settings } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const SCOPE_COLORS = { 1: '#0066FF', 2: '#16A34A', 3: '#D97706' };
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function GHGDashboard() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => { loadSummary(); }, [year]);

  const loadSummary = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/ghg/summary?year=${year}`);
      setSummary(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const scopeData = summary?.by_scope?.map(s => ({
    name: `Scope ${s.scope}`,
    value: Math.round(parseFloat(s.total_co2e) * 100) / 100,
    color: SCOPE_COLORS[s.scope]
  })) || [];

  const monthlyData = MONTHS.map((m, i) => {
    const found = summary?.monthly_trend?.find(t => parseInt(t.month) === i + 1);
    return { month: m, co2e: found ? Math.round(parseFloat(found.total_co2e) * 100) / 100 : 0 };
  });

  const targetProgress = summary?.target ? (() => {
    const t = summary.target;
    const pct = t.base_year_emissions > 0
      ? Math.round(((t.base_year_emissions - summary.total_co2e) / t.base_year_emissions) * 100)
      : 0;
    return { target: t, pct };
  })() : null;

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, color: '#6B7280' }}>Loading...</div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <Leaf size={22} color="#16A34A" />
            <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 24, fontWeight: 800, color: '#0A1628', margin: 0 }}>Carbon & GHG</h1>
          </div>
          <div style={{ fontSize: 14, color: '#6B7280', fontFamily: 'var(--font-body)' }}>Greenhouse gas inventory and carbon management</div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select value={year} onChange={e => setYear(parseInt(e.target.value))}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontFamily: 'var(--font-body)', fontSize: 14, color: '#374151' }}>
            {[2022,2023,2024,2025,2026].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <Link to="/ghg/settings" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid #E2E8F0', textDecoration: 'none', color: '#374151', fontFamily: 'var(--font-body)', fontSize: 14 }}>
            <Settings size={14} /> Settings
          </Link>
          <Link to="/ghg/inventory" className="btn btn-primary" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
            <Leaf size={14} /> Log Emissions
          </Link>
        </div>
      </div>

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
        {[
          { label: 'Total CO2e', value: summary?.total_co2e ? `${summary.total_co2e.toLocaleString()} mt` : '0 mt', color: '#0066FF', bg: '#EFF6FF', icon: Leaf },
          { label: 'Scope 1', value: `${Math.round(parseFloat(summary?.by_scope?.find(s=>s.scope===1)?.total_co2e||0)).toLocaleString()} mt`, color: '#0066FF', bg: '#EFF6FF', icon: TrendingUp },
          { label: 'Scope 2', value: `${Math.round(parseFloat(summary?.by_scope?.find(s=>s.scope===2)?.total_co2e||0)).toLocaleString()} mt`, color: '#16A34A', bg: '#F0FDF4', icon: TrendingDown },
          { label: 'Credit Balance', value: `${summary?.credit_balance?.toLocaleString() || 0} mt`, color: '#D97706', bg: '#FFFBEB', icon: Award },
        ].map((s, i) => (
          <div key={i} className="stat-card" style={{ background: s.bg }}>
            <div style={{ width: 28, height: 28, borderRadius: 6, background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <s.icon size={14} color={s.color} />
            </div>
            <div>
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Main grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>

        {/* Scope breakdown donut */}
        <div className="card" style={{ padding: '18px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div className="section-label" style={{ margin: 0 }}>Emissions by scope</div>
            <Link to="/ghg/inventory" style={{ fontSize: 13, color: '#0066FF', fontFamily: 'var(--font-body)', fontWeight: 600, textDecoration: 'none' }}>View all →</Link>
          </div>
          {scopeData.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: 13, fontFamily: 'var(--font-body)' }}>
              No emissions logged yet.<br />
              <Link to="/ghg/inventory" style={{ color: '#0066FF' }}>Log your first entry →</Link>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
              <ResponsiveContainer width={160} height={160}>
                <PieChart>
                  <Pie data={scopeData} cx={75} cy={75} innerRadius={45} outerRadius={70} dataKey="value">
                    {scopeData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip formatter={(v) => [`${v} mt CO2e`]} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ flex: 1 }}>
                {scopeData.map((s, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: s.color }} />
                      <span style={{ fontSize: 13, color: '#374151', fontFamily: 'var(--font-body)' }}>{s.name}</span>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#0A1628', fontFamily: 'var(--font-body)' }}>{s.value.toLocaleString()} mt</span>
                  </div>
                ))}
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #F1F5F9' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#0A1628', fontFamily: 'var(--font-body)' }}>Total</span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: '#0A1628', fontFamily: 'var(--font-body)' }}>{summary?.total_co2e?.toLocaleString()} mt CO2e</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Monthly trend */}
        <div className="card" style={{ padding: '18px 20px' }}>
          <div className="section-label" style={{ marginBottom: 14 }}>Monthly emissions — {year}</div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={monthlyData}>
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#6B7280' }} />
              <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} />
              <Tooltip formatter={(v) => [`${v} mt CO2e`]} />
              <Bar dataKey="co2e" fill="#0066FF" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bottom grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20 }}>

        {/* Target progress */}
        <div className="card" style={{ padding: '18px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div className="section-label" style={{ margin: 0 }}>Reduction target</div>
            <Link to="/ghg/targets" style={{ fontSize: 13, color: '#0066FF', fontFamily: 'var(--font-body)', fontWeight: 600, textDecoration: 'none' }}>Manage →</Link>
          </div>
          {!targetProgress ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <TrendingDown size={28} color="#CBD5E1" style={{ margin: '0 auto 10px', display: 'block' }} />
              <div style={{ fontSize: 13, color: '#94a3b8', fontFamily: 'var(--font-body)', lineHeight: 1.5, marginBottom: 12 }}>No target set yet</div>
              <Link to="/ghg/targets" style={{ fontSize: 13, color: '#0066FF', fontFamily: 'var(--font-body)', fontWeight: 600, textDecoration: 'none' }}>Set a target →</Link>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 13, color: '#374151', fontFamily: 'var(--font-body)', marginBottom: 8 }}>
                {targetProgress.target.framework} — {targetProgress.target.reduction_pct}% by {targetProgress.target.target_year}
              </div>
              <div style={{ height: 8, background: '#F1F5F9', borderRadius: 4, overflow: 'hidden', marginBottom: 8 }}>
                <div style={{ height: 8, width: `${Math.min(Math.max(targetProgress.pct, 0), 100)}%`, background: targetProgress.pct >= 50 ? '#16A34A' : '#D97706', borderRadius: 4 }} />
              </div>
              <div style={{ fontSize: 12, color: '#6B7280', fontFamily: 'var(--font-body)' }}>
                {targetProgress.pct}% reduction achieved vs base year {targetProgress.target.base_year}
              </div>
            </div>
          )}
        </div>

        {/* Carbon credits */}
        <div className="card" style={{ padding: '18px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div className="section-label" style={{ margin: 0 }}>Carbon credits</div>
            <Link to="/ghg/credits" style={{ fontSize: 13, color: '#0066FF', fontFamily: 'var(--font-body)', fontWeight: 600, textDecoration: 'none' }}>Manage →</Link>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { label: 'Purchased', value: `${summary?.credits_purchased?.toLocaleString() || 0} mt`, color: '#16A34A', bg: '#F0FDF4' },
              { label: 'Retired', value: `${summary?.credits_retired?.toLocaleString() || 0} mt`, color: '#D97706', bg: '#FFFBEB' },
              { label: 'Balance', value: `${summary?.credit_balance?.toLocaleString() || 0} mt`, color: '#0066FF', bg: '#EFF6FF' },
            ].map((s, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: s.bg, borderRadius: 8 }}>
                <span style={{ fontSize: 13, color: '#374151', fontFamily: 'var(--font-body)' }}>{s.label}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: s.color, fontFamily: 'var(--font-body)' }}>{s.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* By facility */}
        <div className="card" style={{ padding: '18px 20px' }}>
          <div className="section-label" style={{ marginBottom: 14 }}>Top emitting facilities</div>
          {!summary?.by_facility?.length ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8', fontSize: 13, fontFamily: 'var(--font-body)' }}>No data yet</div>
          ) : (
            <div>
              {summary.by_facility.slice(0, 4).map((f, i) => {
                const pct = summary.total_co2e > 0 ? Math.round((parseFloat(f.total_co2e) / summary.total_co2e) * 100) : 0;
                return (
                  <div key={i} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#0A1628', fontFamily: 'var(--font-body)' }}>{f.facility_name}</span>
                      <span style={{ fontSize: 11, color: '#6B7280', fontFamily: 'var(--font-body)' }}>{Math.round(parseFloat(f.total_co2e))} mt</span>
                    </div>
                    <div style={{ height: 4, background: '#F1F5F9', borderRadius: 2 }}>
                      <div style={{ height: 4, width: `${pct}%`, background: '#0066FF', borderRadius: 2 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default GHGDashboard;
